import { AlertTriangle, Edit, Image as ImageIcon, List, Package, Plus, Search, Trash2, X, XCircle } from 'lucide-react';
import { ChangeEvent, FormEvent, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useCategories } from '../hooks/useCategories';
import { useProducts } from '../hooks/useProducts';
import type { Category, CreateCategoryInput, UpdateCategoryInput } from '../services/categoryService';
import { CategoryService } from '../services/categoryService';
import { FileService } from '../services/fileService';
import { Product } from '../types/product';

export default function CategoriesManagement() {
  const {
    categories,
    isLoading: isCategoriesLoading,
    error: categoriesError,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategories();

  const {
    products,
    isLoading: isProductsLoading,
    error: productsError,
  } = useProducts();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<CreateCategoryInput>({
    name: '',
    imageUrl: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [selectedCategoryForProducts, setSelectedCategoryForProducts] = useState<Category | null>(null);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await updateCategory.mutateAsync({
          id: selectedCategory.id,
          category: formData as UpdateCategoryInput,
        });
        toast.success('Category updated successfully');
      } else {
        await createCategory.mutateAsync(formData);
        toast.success('Category created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    try {
      await deleteCategory.mutateAsync(categoryToDelete.id);
      toast.success('Category deleted successfully');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      imageUrl: category.imageUrl,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedCategory(null);
    setFormData({
      name: '',
      imageUrl: '',
    });
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size too large. Maximum size is 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const response = await FileService.uploadFiles([file]);
      const uploadedFile = response.files[0];
      setFormData(prev => ({ ...prev, imageUrl: uploadedFile.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleProductSelect = (value: string) => {
    setFormData(prev => ({ ...prev, products: value.split(',') }));
  };

  const handleOpenDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleManageProducts = (category: Category) => {
    console.log('Opening manage products for category:', category);
    console.log('Category products:', category.products);
    console.log('Available products:', products);
    
    // Set the selected category with its current products
    setSelectedCategoryForProducts(category);
    
    // Filter out products that are already in the category
    const available = products?.filter(
      product => !category.products.includes(product.id)
    ) || [];
    setAvailableProducts(available);
    
    // Reset search state
    setSearchQuery('');
    setIsSearching(false);
    
    setProductsDialogOpen(true);
  };

  const handleAddProductToCategory = async (productId: string) => {
    if (!selectedCategoryForProducts) return;

    try {
      await CategoryService.addProductToCategory(selectedCategoryForProducts.id, productId);
      
      // Update local state
      setSelectedCategoryForProducts(prev => prev ? {
        ...prev,
        products: [...prev.products, productId],
      } : null);
      
      // Update available products
      setAvailableProducts(prev => prev.filter(p => p.id !== productId));
      
      toast.success('Product added to category');
    } catch (error) {
      console.error('Error adding product to category:', error);
      toast.error('Failed to add product to category');
    }
  };

  const handleRemoveProductFromCategory = async (productId: string) => {
    if (!selectedCategoryForProducts) return;

    try {
      await CategoryService.removeProductFromCategory(selectedCategoryForProducts.id, productId);
      
      // Update local state
      setSelectedCategoryForProducts(prev => prev ? {
        ...prev,
        products: prev.products.filter(id => id !== productId),
      } : null);
      
      // Add back to available products
      const productToAdd = products?.find(p => p.id === productId);
      if (productToAdd) {
        setAvailableProducts(prev => [...prev, productToAdd]);
      }
      
      toast.success('Product removed from category');
    } catch (error) {
      console.error('Error removing product from category:', error);
      toast.error('Failed to remove product from category');
    }
  };

  const handleCloseProductsDialog = () => {
    setProductsDialogOpen(false);
    setSelectedCategoryForProducts(null);
    setAvailableProducts([]);
    setSearchQuery('');
    setIsSearching(false);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsSearching(true);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setIsSearching(false);
  };

  if (categoriesError || productsError) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-red-500">
          Error: {categoriesError?.message || productsError?.message}
        </div>
      </div>
    );
  }

  const isLoading = isCategoriesLoading || isProductsLoading;

  return (
    <div className="container mx-auto p-4 sm:p-6">
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h1 className="text-2xl font-bold">Categories Management</h1>
            <button
              onClick={handleOpenDialog}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </button>
          </div>

          {categories?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[60vh] bg-white rounded-lg border-2 border-dashed border-gray-200 p-8">
              <Package className="w-16 h-16 text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 mb-2">No Categories Yet</h2>
              <p className="text-gray-500 mb-6 text-center">Start by creating your first category to organize your products</p>
              <button
                onClick={handleOpenDialog}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Category
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Image
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Products
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categories?.map((category) => (
                      <tr key={category.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{category.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {category.imageUrl ? (
                            <img
                              src={category.imageUrl}
                              alt={category.name}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <Package className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">
                              {category.products.length} products
                            </span>
                            <button
                              onClick={() => handleManageProducts(category)}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <List className="h-4 w-4 mr-1" />
                              Manage
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden space-y-4">
                {categories?.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg shadow overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {category.imageUrl ? (
                            <img
                              src={category.imageUrl}
                              alt={category.name}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                              <Package className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <h3 className="text-base font-medium text-gray-900">{category.name}</h3>
                            <p className="text-sm text-gray-500">
                              {category.products.length} products
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleManageProducts(category)}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md"
                            title="Manage Products"
                          >
                            <List className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md"
                            title="Edit Category"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(category)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md"
                            title="Delete Category"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Products Section */}
                    <div className="p-4 bg-gray-50">
                      <button
                        onClick={() => handleManageProducts(category)}
                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <List className="h-4 w-4 mr-2" />
                        Manage Products
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Products Management Dialog */}
          {productsDialogOpen && selectedCategoryForProducts && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleCloseProductsDialog}
              />
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
                  <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold truncate">
                        Manage Products - {selectedCategoryForProducts.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedCategoryForProducts.products.length} products in this category
                      </p>
                    </div>
                    <button
                      onClick={handleCloseProductsDialog}
                      className="p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-100 ml-4"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-4">
                    {/* Search Bar */}
                    <div className="mb-4">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={handleSearch}
                          placeholder="Search products..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchQuery && (
                          <button
                            onClick={handleSearchClear}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Available Products */}
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Available Products</h4>
                      <div className="border rounded-lg divide-y max-h-[40vh] overflow-y-auto">
                        {isProductsLoading ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            Loading products...
                          </div>
                        ) : availableProducts.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            No available products to add
                          </div>
                        ) : (
                          availableProducts
                            .filter(product => 
                              !searchQuery || 
                              product.title.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map((product) => (
                              <div key={product.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                                <div className="flex items-center flex-1 min-w-0">
                                  {product.images?.[0] && (
                                    <img
                                      src={product.images[0]}
                                      alt={product.title}
                                      className="h-8 w-8 rounded-md object-cover mr-3 flex-shrink-0"
                                    />
                                  )}
                                  <span className="text-sm text-gray-900 truncate">{product.title}</span>
                                </div>
                                <button
                                  onClick={() => handleAddProductToCategory(product.id)}
                                  className="flex-shrink-0 p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md ml-2"
                                  title="Add to category"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            ))
                        )}
                      </div>
                    </div>

                    {/* Current Products */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Products in Category</h4>
                      <div className="border rounded-lg divide-y max-h-[40vh] overflow-y-auto">
                        {isProductsLoading ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            Loading products...
                          </div>
                        ) : selectedCategoryForProducts.products.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500 text-center">
                            No products in this category
                          </div>
                        ) : (
                          products
                            ?.filter(product => 
                              selectedCategoryForProducts.products.includes(product.id) &&
                              (!searchQuery || product.title.toLowerCase().includes(searchQuery.toLowerCase()))
                            )
                            .map((product) => (
                              <div key={product.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                                <div className="flex items-center flex-1 min-w-0">
                                  {product.images?.[0] && (
                                    <img
                                      src={product.images[0]}
                                      alt={product.title}
                                      className="h-8 w-8 rounded-md object-cover mr-3 flex-shrink-0"
                                    />
                                  )}
                                  <span className="text-sm text-gray-900 truncate">{product.title}</span>
                                </div>
                                <button
                                  onClick={() => handleRemoveProductFromCategory(product.id)}
                                  className="flex-shrink-0 p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md ml-2"
                                  title="Remove from category"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end p-4 border-t">
                    <button
                      type="button"
                      onClick={handleCloseProductsDialog}
                      className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          {deleteDialogOpen && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={handleDeleteCancel}
              />

              {/* Dialog */}
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-red-100 rounded-full">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Delete Category
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Are you sure you want to delete this category?
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleDeleteCancel}
                      className="p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-100"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <p className="text-sm text-red-700">
                        This action cannot be undone. This will permanently delete the category
                        {categoryToDelete?.products.length ? ` and remove it from ${categoryToDelete.products.length} associated product${categoryToDelete.products.length === 1 ? '' : 's'}.` : '.'}
                      </p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 p-4 border-t">
                    <button
                      type="button"
                      onClick={handleDeleteCancel}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteConfirm}
                      disabled={deleteCategory.isPending}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleteCategory.isPending ? 'Deleting...' : 'Delete Category'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Custom Dialog */}
          {isDialogOpen && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={() => setIsDialogOpen(false)}
              />

              {/* Dialog */}
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {selectedCategory ? 'Edit Category' : 'Add New Category'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedCategory 
                          ? 'Update your category details below'
                          : 'Fill in the details to create a new category'}
                      </p>
                    </div>
                    <button
                      onClick={() => setIsDialogOpen(false)}
                      className="p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-100"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        placeholder="Enter category name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                        Category Image
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            id="image"
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById('image')?.click()}
                            disabled={isUploading}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ImageIcon className="w-4 h-4 mr-2 inline" />
                            {isUploading ? 'Uploading...' : 'Upload Image'}
                          </button>
                        </div>
                        {formData.imageUrl && (
                          <div className="relative w-32 h-32 border rounded-lg overflow-hidden">
                            <img
                              src={formData.imageUrl}
                              alt="Category preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => setIsDialogOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={createCategory.isPending || updateCategory.isPending}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {selectedCategory
                          ? updateCategory.isPending
                            ? 'Updating...'
                            : 'Update Category'
                          : createCategory.isPending
                            ? 'Creating...'
                            : 'Create Category'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
} 